import { useEffect, useState } from 'react';
import { useTheme, Box, CircularProgress, Divider, Link, Stack, Typography } from '@mui/material';
import { z as zod } from 'zod';
import DOMPurify from 'dompurify';
import useReCaptcha from '@hooks/useReCaptcha';
import { getAgencyDirectoryAgencies, getAgencyDirectoryStates, publicAddAgencyOfficial } from '@networkRequest';
import { createDynamicStringValidator, createOptionalPhoneValidator } from '../../../utils/validationTools';
import { NewOfficialData } from '../../../utils/types';
import { Button, TextInput } from '@baseComponents';
import Acknowledgement from './Acknowledgement';
import StateAgencySelect from './StateAgencySelect';
import { ShieldHelpIcon } from '../../../assets/icons/icons';

const STATES_ERROR = 'Failed to load states.';
const AGENCIES_ERROR = 'Failed to load agencies.';
const SUBMISSION_ERROR = 'We are unable to complete your registration at this time, please contact support at help@surepass.com';

export interface CustomErrorMap {
  [inputName: string]: string[];
}

const newOfficialInit: NewOfficialData = {
  first_name: '',
  last_name: '',
  title: '',
  rank: '',
  badge_number: '',
  agency_name: '',
  email: '',
  phone_number: '',
  post_id: '',
  acknowledgementChecked: false,
};

interface RegistrationFormProps {
  // Called with the email the official registered with
  onSuccess: (email: string) => void;
  // Sent with the registration when the form is shown as part of a campaign
  promoName?: string;
}

const RegistrationForm = ({
  onSuccess,
  promoName,
}: RegistrationFormProps) => {
  const theme = useTheme();

  const { loading: reCaptchaLoading, generateReCaptchaToken } = useReCaptcha();
  const [formSubmissionLoading, setFormSubmissionLoading] = useState(false);
  const [statesLoading, setStatesLoading] = useState(true);

  const [formData, setFormData] = useState<NewOfficialData>(newOfficialInit);
  // The state only narrows the agency list; it is not sent with the registration
  const [selectedState, setSelectedState] = useState('');
  const [states, setStates] = useState<string[]>([]);
  // Each state's agencies are fetched the first time the state is picked
  const [agenciesByState, setAgenciesByState] = useState<Record<string, string[]>>({});
  const [failedState, setFailedState] = useState<string | null>(null);
  const agencies = agenciesByState[selectedState] ?? [];
  const agenciesLoading = !!selectedState
    && agenciesByState[selectedState] === undefined
    && failedState !== selectedState;

  const [formErrors, setFormErrors] = useState<CustomErrorMap>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data, error } = await getAgencyDirectoryStates();
        if (data) {
          setStates(data);
        }
        if (error) {
          setSubmissionError(STATES_ERROR)
        }
      } catch {
        setSubmissionError(STATES_ERROR);
      } finally {
        setStatesLoading(false);
      }
    };

    fetchData();
  }, []);

  const officialValidationSchema = zod.object({
    first_name: zod.string().min(1, 'First Name is required.'),
    last_name: zod.string().min(1, 'Last name is required.'),
    title: zod.string().optional(),
    email: zod.string().email(),
    badge_number: zod.string().min(1, 'Badge is required'),
    rank: zod.string().min(1, 'Rank is required.'),
    phone_number: createOptionalPhoneValidator(),
    agency_name: createDynamicStringValidator(agencies),
  });

  const validateForm = () => {
    try {
      if (!formData.acknowledgementChecked) return false;
      officialValidationSchema.parse(formData);
      setFormErrors({});
      return true;
    } catch (formErrors) {
      if (formErrors instanceof zod.ZodError) {
        const formattedFormErrors: CustomErrorMap = formErrors.issues.reduce((acc, issue) => {
          const inputName = issue.path.join('.');
          return {
            ...acc,
            [inputName]: (acc[inputName] || []).concat(issue.message)
          };
        }, {} as CustomErrorMap);
        setFormErrors(formattedFormErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmissionLoading(true);

    const validForm = validateForm();

    if (!validForm) {
      setFormSubmissionLoading(false);
      return;
    }

    try {
      const reCaptchaResult = await generateReCaptchaToken('signup');
      if ('token' in reCaptchaResult) {
        const { status, error } = await publicAddAgencyOfficial({
          agency_official: {
            ...formData,
            google_recaptcha_token: reCaptchaResult.token,
            ...(promoName ? { promo_name: promoName } : {}),
          },
        });

        if (status === 204) {
          onSuccess(formData.email);
          setFormData(newOfficialInit);
          setSelectedState('');
        }

        if (error) {
          setSubmissionError(SUBMISSION_ERROR);
        }
      } else if ('error' in reCaptchaResult) {
        setSubmissionError(SUBMISSION_ERROR);
      }
    } catch {
      setSubmissionError(SUBMISSION_ERROR);
    }
    finally {
      setFormSubmissionLoading(false);
    }
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = DOMPurify.sanitize(value);
    setFormData({ ...formData, [name]: sanitizedValue });
  };

  const fetchAgencies = async (state: string) => {
    try {
      const { data } = await getAgencyDirectoryAgencies(state);
      if (data) {
        setAgenciesByState((prev) => ({ ...prev, [state]: data }));
        return;
      }
    } catch {
      // Reported below, the same as an error response
    }
    setFailedState(state);
    setSubmissionError(AGENCIES_ERROR);
  };

  // A different state has a different agency list, so the agency is cleared
  const handleStateChange = (state: string) => {
    setSelectedState(state);
    setFormData({ ...formData, agency_name: '' });

    if (state && agenciesByState[state] === undefined) {
      setFailedState(null);
      fetchAgencies(state);
    }
  };

  const handleAgencyChange = (agencyName: string) => {
    setFormData({ ...formData, agency_name: DOMPurify.sanitize(agencyName) });
  };

  const inputs = {
    first_name: {
      name: 'first_name',
      label: 'First Name',
      placeholder: 'First Name',
      value: formData.first_name,
      required: true
    },
    last_name: {
      name: 'last_name',
      label: 'Last Name (Required)',
      placeholder: 'Last Name',
      value: formData.last_name,
      required: true
    },
    rank: {
      name: 'rank',
      label: 'Rank (Required)',
      placeholder: 'Police Sergeant',
      value: formData.rank,
      required: true
    },
    badge_number: {
      name: 'badge_number',
      label: 'Badge (Required)',
      placeholder: '1528',
      value: formData.badge_number,
      required: true
    },
    email: {
      name: 'email',
      label: 'Work Email Address',
      placeholder: 'name@email.com',
      value: formData.email,
      required: true
    },
    phone_number: {
      name: 'phone_number',
      label: 'Phone Number',
      placeholder: '555-123-4567',
      value: formData.phone_number,
    },
    post_id: {
      name: 'post_id',
      label: 'POST ID Number',
      placeholder: '',
      value: formData.post_id,
      required: true
    },
    acknowledgement: {
      name: 'acknowledgement',
      label: 'Acknowledgement',
      value: formData.acknowledgementChecked,
      required: true,
    },
  };

  const styles = {
    container: {
      padding: '16px',
      borderRadius: '8px',
      backgroundColor: theme.palette.background.default,
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: '24px',
      paddingBottom: '24px',
    },
    title: {
      fontWeight: 500,
      fontSize: '32px',
      lineHeight: '140%',
      color: theme.palette.text.primary,
    },
    description1: {
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '140%',
      color: theme.palette.text.primary,
    },
    description2: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '140%',
      color: theme.palette.text.primary,
    },
    inputLabel: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '140%',
      color: theme.palette.text.primary,
    },
    button: {
      borderRadius: '10px',
      background: theme.palette.text.primary,
      color: '#FFF',
      fontWeight: 600,
      fontSize: '16px',
      textTransform: 'none',
      marginTop: '16px',
      paddingRight: '22px',
      paddingLeft: '22px',
    },
  };

  const isSubmitDisabled =
    !formData.acknowledgementChecked ||
    !formData.first_name ||
    !formData.last_name ||
    !formData.rank ||
    !formData.badge_number ||
    !formData.email ||
    !selectedState ||
    !formData.agency_name;

  return (
    <Stack direction='column' sx={styles.container}>
      <Stack direction='column' sx={{
        alignItems: 'center'
      }}>
        <Typography sx={styles.title}>Create Your OCID</Typography>
      </Stack>
      <Stack direction='column' sx={{
        mt: '16px'
      }}>
        <Typography sx={styles.description1}>For Law Enforcement Officials Only</Typography>
        <Typography
          sx={[{
            mt: '8px'
          }, styles.description2]}>Please complete the form below. </Typography>
        <Typography
          sx={[{
            mt: '16px'
          }, styles.inputLabel]}>* Required fields</Typography>
      </Stack>
      {reCaptchaLoading || statesLoading
        ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={40} thickness={3} />
          </Box>
        )
        : (
          <Stack
            direction='column'
            component='form'
            onSubmit={handleSubmit}
            role='form'
            sx={{
              alignItems: 'center'
            }}
          >
            <TextInput
              name={inputs.first_name.name}
              label={inputs.first_name.label}
              placeholder={inputs.first_name.placeholder}
              value={formData.first_name}
              error={!!formErrors.first_name}
              onChange={handleTextInputChange}
              required
              style={{ marginTop: '16px' }}
            />
            <TextInput
              name={inputs.last_name.name}
              label={inputs.last_name.label}
              placeholder={inputs.last_name.placeholder}
              value={formData.last_name}
              error={!!formErrors.last_name}
              onChange={handleTextInputChange}
              required
              style={{ marginTop: '16px' }}
            />
            <TextInput
              name={inputs.rank.name}
              label={inputs.rank.label}
              placeholder={inputs.rank.placeholder}
              value={formData.rank}
              error={!!formErrors.rank}
              onChange={handleTextInputChange}
              required
              style={{ marginTop: '16px' }}
            />
            <TextInput
              name={inputs.badge_number.name}
              label={inputs.badge_number.label}
              placeholder={inputs.badge_number.placeholder}
              value={formData.badge_number}
              error={!!formErrors.badge_number}
              onChange={handleTextInputChange}
              required
              style={{ marginTop: '16px' }}
            />
            <StateAgencySelect
              states={states}
              agencies={agencies}
              agenciesLoading={agenciesLoading}
              state={selectedState}
              agencyName={formData.agency_name}
              onStateChange={handleStateChange}
              onAgencyChange={handleAgencyChange}
            />
            <TextInput
              name={inputs.email.name}
              label={inputs.email.label}
              placeholder={inputs.email.placeholder}
              value={formData.email}
              error={!!formErrors.email}
              onChange={handleTextInputChange}
              required
              style={{ marginTop: '16px' }}
            />
            <TextInput
              name={inputs.phone_number.name}
              label={inputs.phone_number.label}
              placeholder={inputs.phone_number.placeholder}
              value={formData.phone_number}
              error={!!formErrors.phone_number}
              onChange={handleTextInputChange}
              style={{ marginTop: '16px' }}
            />
            <TextInput
              name={inputs.post_id.name}
              label={inputs.post_id.label}
              placeholder={inputs.post_id.placeholder}
              value={formData.post_id}
              error={!!formErrors.post_id}
              onChange={handleTextInputChange}
              style={{ marginTop: '16px' }}
            />
            <Acknowledgement
              checked={formData.acknowledgementChecked}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                acknowledgementChecked: e.target.checked
              }))}
              gutterTop
            />
            <Typography
              sx={[{
                mt: '16px'
              }, styles.description2]}>
              Once submitted, SurePass will review your information and email you further instructions.
            </Typography>
            {
              submissionError
                ? (
                  <Typography
                    sx={[{
                      my: '16px'
                    }, styles.description2, { color: theme.palette.error.main }]}>{submissionError}</Typography>
                )
                : null
            }
            <Button
              type='submit'
              text='Create Account'
              loading={formSubmissionLoading}
              sx={{ marginTop: '16px' }}
              disabled={isSubmitDisabled}
            />
          </Stack>
        )
      }
      <Divider sx={{ marginTop: '40px' }} />
      <Stack
        direction='row'
        sx={{
          alignItems: 'center',
          mt: '16px'
        }}>
        <ShieldHelpIcon width='16px' height='20px' />
        <Typography sx={[styles.description2, { marginLeft: '8px' }]}>
          For registration or credential verification difficulties, <Link href='mailto:help@surepass.com' color={theme.palette.text.primary} sx={{ fontWeight: 500 }}>contact support</Link>.
        </Typography>
      </Stack>
      <Typography
        sx={[{
          mt: '16px'
        }, styles.description2, { fontWeight: 500 }]}>
        Not a law enforcement officer? <Typography component='span' sx={[styles.description2, { fontWeight: 400 }]}>Contact us at </Typography><Link href='mailto:sales@surepass.com' color={theme.palette.text.primary} sx={{ fontWeight: 500 }}>sales@surepass.com</Link>.
      </Typography>
    </Stack >
  );
};

export default RegistrationForm;